import React, { FC, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../api';
import {
  CostDimension,
  CostDimensionFilters,
  CostFilterBar,
  dimensionFilterParams,
  hasEmptySelection,
} from './CostDimensionFilters';
import { formatUSD, SERIES_COLORS } from './platformStatsChartUtils';

/**
 * "Model Breakdown (Window Total)" table on the Costs tab: calls, cost and
 * cache hit rate per model display name, narrowed to any subset of triggers
 * and agent type + node pairs. Cache hit rate depends heavily on both, so
 * the filters make the per-model figure meaningful for one workload at a
 * time. Data comes from `GET /platform-stats/model-breakdown`, which shares
 * the page's date range and user filter via `baseQuery`.
 */

/** Dimensions the table can be filtered by (it is already split by model). */
export const TABLE_FILTER_DIMENSIONS: CostDimension[] = ['trigger', 'agent'];

export interface ModelBreakdownRow {
  model: string;
  calls: number;
  cost: number;
  inputTokens: number;
  inputTokensSinceCaching: number;
  cacheWriteTokens: number;
  cacheReadTokens: number;
  netSavings: number;
}

export interface ModelBreakdownResponse {
  success: boolean;
  filters: CostDimensionFilters;
  rows: ModelBreakdownRow[];
  totals: Omit<ModelBreakdownRow, 'model'>;
  options: Record<CostDimension, string[]>;
}

/** Query string for the endpoint, given the page-level range/user fragment. */
export function modelBreakdownQuery(
  baseQuery: string,
  filters: CostDimensionFilters,
): string {
  const params = dimensionFilterParams(filters, TABLE_FILTER_DIMENSIONS);
  return params.length ? `${baseQuery}&${params.join('&')}` : baseQuery;
}

/** Cache read tokens over input tokens logged since the model started caching. */
export function cacheHitRate(
  row: Pick<ModelBreakdownRow, 'cacheReadTokens' | 'inputTokensSinceCaching'>,
): string {
  if (row.inputTokensSinceCaching === 0) return '—';
  return `${((row.cacheReadTokens / row.inputTokensSinceCaching) * 100).toFixed(1)}%`;
}

interface ModelBreakdownTableProps {
  /** `startDate=…&endDate=…` or `days=N`, plus any `&filterUserId=…`. */
  baseQuery: string;
  /** Owned by the parent so it survives the Costs tab re-mounting. */
  filters: CostDimensionFilters;
  onFiltersChange: (filters: CostDimensionFilters) => void;
}

const ModelBreakdownTable: FC<ModelBreakdownTableProps> = ({
  baseQuery,
  filters,
  onFiltersChange,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [data, setData] = useState<ModelBreakdownResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const emptySelection = hasEmptySelection(filters, TABLE_FILTER_DIMENSIONS);

  useEffect(() => {
    if (emptySelection) {
      setLoading(false);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const token = await getAccessTokenSilently();
        const res = await api.get<ModelBreakdownResponse>(
          `/platform-stats/model-breakdown?${modelBreakdownQuery(baseQuery, filters)}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (cancelled) return;
        setData(res.data);
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to load model breakdown:', err);
        setError('Failed to load model breakdown.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getAccessTokenSilently, baseQuery, filters, emptySelection]);

  const rows = emptySelection ? [] : (data?.rows ?? []);
  const totals = data?.totals;
  const totalCost = totals?.cost ?? 0;

  return (
    <div className='costs-breakdown model-breakdown'>
      <div className='breakdown-header'>
        <div className='breakdown-title'>Model Breakdown (Window Total)</div>
        <CostFilterBar
          dimensions={TABLE_FILTER_DIMENSIONS}
          options={data?.options}
          filters={filters}
          onFiltersChange={onFiltersChange}
        />
      </div>
      {error ? (
        <div className='msg error'>{error}</div>
      ) : !data && loading ? (
        <div className='loading'>Loading model breakdown...</div>
      ) : rows.length === 0 ? (
        <div className='loading'>No cost data for this selection.</div>
      ) : (
        <table className={`costs-table${loading ? ' reloading' : ''}`}>
          <thead>
            <tr>
              <th>Model</th>
              <th>Calls</th>
              <th>Total Cost</th>
              <th>% of Spend</th>
              <th>Avg per Call</th>
              <th>Cache Hit %</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((m, i) => (
              <tr key={m.model}>
                <td className='model-cell'>
                  <span
                    className='outcome-dot'
                    style={{
                      background: SERIES_COLORS[i % SERIES_COLORS.length],
                    }}
                  />
                  {m.model}
                </td>
                <td>{m.calls.toLocaleString()}</td>
                <td>{formatUSD(m.cost)}</td>
                <td>
                  {totalCost > 0
                    ? `${((m.cost / totalCost) * 100).toFixed(1)}%`
                    : '—'}
                </td>
                <td>{m.calls > 0 ? formatUSD(m.cost / m.calls) : '—'}</td>
                <td>{cacheHitRate(m)}</td>
              </tr>
            ))}
            {rows.length > 1 && totals && (
              <tr className='totals-row'>
                <td>
                  <strong>Total</strong>
                </td>
                <td>
                  <strong>{totals.calls.toLocaleString()}</strong>
                </td>
                <td>
                  <strong>{formatUSD(totals.cost)}</strong>
                </td>
                <td>
                  <strong>100%</strong>
                </td>
                <td>
                  <strong>
                    {totals.calls > 0
                      ? formatUSD(totals.cost / totals.calls)
                      : '—'}
                  </strong>
                </td>
                <td>
                  <strong>{cacheHitRate(totals)}</strong>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default ModelBreakdownTable;
